import { useRef } from 'react';
import styled from 'styled-components';
import { Canvas, useFrame } from '@react-three/fiber';
import { View, OrbitControls, PerspectiveCamera } from '@react-three/drei';
import { technologyTranslations } from '../../locales/technologyTranslations';

// import models
import { Motion } from '../3D/Motion';
import { Sentiment } from '../3D/Sentiment';

const CompanyTab = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  width: 100%;
  text-align: center;
  margin-bottom: 80px;
`;

const Headline = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: white;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 120px;
  width: 100%;
`;

const Section = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  width: 100%;

  &:nth-child(even) {
    flex-direction: row-reverse;
  }

  @media (max-width: 1024px) {
    flex-direction: column !important;
    gap: 20px;
    text-align: center;
  }
`;

const ContentBlock = styled.div`
  flex: 1;
  padding: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 2rem;
  color: white;
  margin: 0 0 20px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const SectionText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Panel = styled(View)`
  width: 600px;
  height: 400px;
  border-radius: 15px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0);
  overflow: hidden;

  @media (max-width: 1024px) {
    width: 80%;
    height: 300px;
  }
`;

const StyledCanvas = styled(Canvas)`
  position: fixed !important;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: -1;
`;


const Scene1 = () => {
  return (
    <>
      <hemisphereLight intensity={1} />
      <directionalLight position={[1, 1, 1]} intensity={1.5} />
      <PerspectiveCamera
        makeDefault
        position={[0, 3, 1.6]}
        fov={50}
        near={0.001}
        far={1000}
      />
      <Motion />
      <OrbitControls 
        enableZoom={false}
        minPolarAngle={Math.PI / 4}
        maxPolarAngle={Math.PI * 3/4}
        target={[0, 0, 0]}
        minDistance={0.1}
        maxDistance={10}
      />
    </>
  );
};

const Scene2 = () => {
  return (
    <>
      <hemisphereLight intensity={1} />
      <directionalLight position={[1, 1, 1]} intensity={1.5} />
      <PerspectiveCamera
        makeDefault
        position={[0, 0, 0.35]}
        fov={45}
      />
      <Sentiment />
      <OrbitControls 
        enableZoom={false}
        minPolarAngle={Math.PI / 4}
        maxPolarAngle={Math.PI * 3/4}
        target={[0, 0, 0]}
      />
    </>
  );
};

const Scene3 = () => {
  const meshRef = useRef();

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.rotation.y += 0.01;
      meshRef.current.rotation.x = Math.PI / 4 + Math.sin(state.clock.elapsedTime) * 0.1;
    }
  });

  return (
    <>
      <hemisphereLight intensity={1} />
      <directionalLight position={[1, 1, 1]} intensity={1.5} />
      <PerspectiveCamera
        makeDefault
        position={[0, 0, 4]}
        fov={45}
      />
      <mesh ref={meshRef} rotation={[Math.PI / 4, 0, 0]}>
        <octahedronGeometry />
        <meshStandardMaterial color="orange" wireframe />
      </mesh>
      <OrbitControls 
        enableZoom={false}
        minPolarAngle={Math.PI / 4}
        maxPolarAngle={Math.PI * 3/4}
        target={[0, 0, 0]}
      />
    </>
  );
};

function TechnologyTab({ currentLang }) {
  const { headline, sections } = technologyTranslations[currentLang];
  const container = useRef();

  return (
    <CompanyTab ref={container}>
      <Header>
        <Headline>{headline}</Headline>
      </Header>

      <SectionsContainer>
        <Section>
          <ContentBlock>
            <SectionTitle>{sections[0].title}</SectionTitle>
            <SectionText>{sections[0].content}</SectionText>
          </ContentBlock>
          <Panel>
            <Scene1 />
          </Panel>
        </Section>

        <Section>
          <ContentBlock>
            <SectionTitle>{sections[1].title}</SectionTitle>
            <SectionText>{sections[1].content}</SectionText>
          </ContentBlock>
          <Panel>
            <Scene2 />
          </Panel>
        </Section>

        <Section>
          <ContentBlock>
            <SectionTitle>{sections[2].title}</SectionTitle>
            <SectionText>{sections[2].content}</SectionText>
          </ContentBlock>
          <Panel>
            <Scene3 />
          </Panel>
        </Section>
      </SectionsContainer>

      <StyledCanvas eventSource={container} style={{ position: 'fixed' }}>
        <View.Port />
      </StyledCanvas>
    </CompanyTab>
  );
}

export default TechnologyTab;